import React, { useState } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'
import LocationPreview from './LocationPreview'
import {
  SECTIONS,
  contested,
  coursesOf,
  getValue,
  hasValue,
  valueOf,
  withValue,
} from './survivorDraft'

// Picking the surviving course's fields out of the whole group.
//
// A row per field, a column per course, and a Result column. Clicking a course's cell copies that
// value into Result; Result is also directly editable, because the best answer is quite often
// neither course's — two half-written descriptions make one good one, and no picker can produce
// that.
//
// The read-only member table above this stays where it is. It is the evidence the merge is right at
// all, and it should not turn into a form: deciding *whether* these are the same place and deciding
// *what the surviving place is called* are two questions, asked in that order.
//
// What is shown by default is only what needs deciding — the fields the group disagrees about.
// Everything else is one toggle away. A group of two courses carries some seventy fields, and a
// wall of identical rows is how you stop reading the six that matter.

const BOOL_OPTIONS = [
  { value: '', label: 'unknown' },
  { value: 'true', label: 'yes' },
  { value: 'false', label: 'no' },
]

/** A value as a human reads it in a cell. Null for "this course says nothing". */
const format = (field, value) => {
  if (!hasValue(field, value)) return null
  switch (field.type) {
    case 'multi':
      return value.map((id) => field.options[id] || `#${id}`).join(', ')
    case 'enum':
      return field.options[value] || `#${value}`
    case 'bool':
      return value ? 'yes' : 'no'
    case 'location':
      return `${value.lat.toFixed(4)}, ${value.lng.toFixed(4)}`
    default:
      return String(value)
  }
}

function ResultControl({ field, value, onChange }) {
  const set = (v) => onChange(v)

  switch (field.type) {
    case 'block':
      return (
        <Form.Control
          as="textarea"
          rows={2}
          size="sm"
          value={value ?? ''}
          onChange={(e) => set(e.target.value === '' ? null : e.target.value)}
        />
      )

    case 'enum':
      return (
        <Form.Select
          size="sm"
          value={value ?? ''}
          onChange={(e) => set(e.target.value === '' ? null : Number(e.target.value))}
        >
          <option value="">—</option>
          {Object.entries(field.options).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </Form.Select>
      )

    case 'bool':
      return (
        <Form.Select
          size="sm"
          value={value === true ? 'true' : value === false ? 'false' : ''}
          onChange={(e) => set(e.target.value === '' ? null : e.target.value === 'true')}
        >
          {BOOL_OPTIONS.map((option) => (
            <option key={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      )

    case 'multi':
      return (
        <div className="curation-survivor-multi">
          {Object.entries(field.options).map(([id, label]) => {
            const ids = value || []
            const checked = ids.includes(Number(id))
            return (
              <Form.Check
                key={id}
                inline
                type="checkbox"
                id={`${field.key}-${id}`}
                label={label}
                checked={checked}
                onChange={() =>
                  set(
                    (checked
                      ? ids.filter((v) => v !== Number(id))
                      : [...ids, Number(id)]
                    ).sort((a, b) => a - b)
                  )
                }
              />
            )
          })}
        </div>
      )

    case 'location':
      return (
        <div className="d-flex gap-1">
          <Form.Control
            size="sm"
            type="number"
            step="0.000001"
            placeholder="lat"
            value={value?.lat ?? ''}
            onChange={(e) =>
              set(e.target.value === '' ? null : { lat: Number(e.target.value), lng: value?.lng ?? 0 })
            }
          />
          <Form.Control
            size="sm"
            type="number"
            step="0.000001"
            placeholder="lng"
            value={value?.lng ?? ''}
            onChange={(e) =>
              set(e.target.value === '' ? null : { lat: value?.lat ?? 0, lng: Number(e.target.value) })
            }
          />
        </div>
      )

    case 'number':
    case 'year':
      return (
        <Form.Control
          size="sm"
          type="number"
          value={value ?? ''}
          onChange={(e) => set(e.target.value === '' ? null : Number(e.target.value))}
        />
      )

    default:
      return (
        <Form.Control
          size="sm"
          value={value ?? ''}
          onChange={(e) => set(e.target.value === '' ? null : e.target.value)}
        />
      )
  }
}

function FieldRow({ group, field, draft, onChange }) {
  const result = getValue(draft, field)
  const resultJson = JSON.stringify(result ?? null)

  return (
    <tr>
      <th scope="row" className="small fw-normal">
        {field.label}
      </th>
      {coursesOf(group).map((course) => {
        const value = valueOf(course, field)
        const text = format(field, value)
        const isResult = text !== null && JSON.stringify(value) === resultJson
        return (
          <td
            key={course.courseId}
            className={`small curation-survivor-candidate ${isResult ? 'is-picked' : ''} ${
              text === null ? 'is-empty' : ''
            }`}
            // A cell with nothing in it is not a choice: picking it would only be a way to blank a
            // field by accident. Blanking is done in the Result control, deliberately.
            onClick={text === null ? undefined : () => onChange(withValue(draft, field, value))}
            title={text === null ? 'nothing to take from this course' : `Use: ${text}`}
          >
            {text ?? <span className="text-muted">—</span>}
          </td>
        )
      })}
      <td className="curation-survivor-result">
        <ResultControl
          field={field}
          value={result}
          onChange={(value) => onChange(withValue(draft, field, value))}
        />
        {field.type === 'location' && hasValue(field, result) && (
          <LocationPreview
            value={result}
            candidates={coursesOf(group)
              .map((course) => ({ courseId: course.courseId, location: valueOf(course, field) }))
              .filter((candidate) => hasValue(field, candidate.location))}
          />
        )}
      </td>
    </tr>
  )
}

export default function SurvivorEditor({ group, draft, onChange, onCombine, onReset }) {
  const [showAll, setShowAll] = useState(false)
  const courses = coursesOf(group)

  // What needs a decision: the group disagrees, or the draft has already moved this field. The
  // second half matters -- a field you edited by hand must not vanish because the courses agreed.
  const needsDecision = (field) =>
    contested(group, field) ||
    JSON.stringify(getValue(draft, field) ?? null) !==
      JSON.stringify(valueOf(group.survivor, field) ?? null)

  const sections = SECTIONS.map((section) => ({
    ...section,
    shown: section.fields.filter((field) => showAll || needsDecision(field)),
  })).filter((section) => section.shown.length > 0)

  const hidden =
    SECTIONS.flatMap((s) => s.fields).length - sections.reduce((n, s) => n + s.shown.length, 0)

  return (
    <div className="curation-survivor">
      <div className="d-flex gap-2 mb-2 align-items-center flex-wrap">
        <Button size="sm" variant="outline-primary" onClick={onCombine}>
          Fill from all courses
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={onReset}>
          Reset to survivor
        </Button>
        {hidden > 0 && (
          <Button size="sm" variant="link" onClick={() => setShowAll(true)}>
            show {hidden} fields every course agrees on
          </Button>
        )}
        {showAll && (
          <Button size="sm" variant="link" onClick={() => setShowAll(false)}>
            only what needs deciding
          </Button>
        )}
      </div>

      {sections.map((section) => (
        <Table key={section.title} size="sm" responsive className="curation-survivor-table mb-3">
          <thead>
            <tr>
              <th className="small">{section.title}</th>
              {courses.map((course) => (
                <th key={course.courseId} className="small fw-normal">
                  #{course.courseId}
                  <div className="text-muted text-truncate">{course.name}</div>
                </th>
              ))}
              <th className="small">Survivor</th>
            </tr>
          </thead>
          <tbody>
            {section.shown.map((field) => (
              <FieldRow
                key={field.key}
                group={group}
                field={field}
                draft={draft}
                onChange={onChange}
              />
            ))}
          </tbody>
        </Table>
      ))}
    </div>
  )
}
